/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1785974400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu Aug 06 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "synapse",
        type: "source",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Azure firewall setup",
          "depth": 0,
          "id": "azure-firewall-setup"
        }, {
          "value": "Connection configuration",
          "depth": 0,
          "id": "connection-configuration"
        }, {
          "value": "Choose authentication method",
          "depth": 1,
          "id": "choose-authentication-method"
        }, {
          "value": "Choose connection type",
          "depth": 1,
          "id": "choose-connection-type"
        }, {
          "value": "Configure your source",
          "depth": 1,
          "id": "configure-your-source"
        }, {
          "value": "Password",
          "depth": 1,
          "id": "password"
        }, {
          "value": "Microsoft Service Principal",
          "depth": 1,
          "id": "microsoft-service-principal"
        }, {
          "value": "Connection String",
          "depth": 1,
          "id": "connection-string"
        }, {
          "value": "Choose your sync engine",
          "depth": 1,
          "id": "choose-your-sync-engine"
        }, {
          "value": "Test your connection",
          "depth": 0,
          "id": "test-your-connection"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      ol: "ol",
      li: "li",
      a: "a",
      strong: "strong",
      img: "img",
      h3: "h3",
      ul: "ul",
      h4: "h4",
      code: "code",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {ConnectionTypeSnippet, SyncEngineSnippet, EngineComparisonTable, NoCommonErrors} = _components;
    if (!ConnectionTypeSnippet) _missingMdxReference("ConnectionTypeSnippet", true);
    if (!EngineComparisonTable) _missingMdxReference("EngineComparisonTable", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncEngineSnippet) _missingMdxReference("SyncEngineSnippet", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you pull data stored in Azure Synapse and push it to downstream destinations. Connecting Hightouch to Azure Synapse requires some setup in both platforms. In Azure Synapse, you need to configure your firewall settings."
      }), "\n", _jsx(_components.h2, {
        id: "azure-firewall-setup",
        children: "Azure firewall setup"
      }), "\n", _jsx(_components.p, {
        children: "By default, Azure protects your Synapse data from any external IP address from connecting to your Synapse workspace. Follow these steps to add Hightouch's IP addresses to the firewall."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Log into your ", _jsx(_components.a, {
            href: "https://portal.azure.com/#home",
            children: "Azure Console Dashboard"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click on your Synapse workspace under ", _jsx(_components.strong, {
            children: "Resources"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["In the left sidebar, select ", _jsx(_components.strong, {
            children: "Security"
          }), " > ", _jsx(_components.strong, {
            children: "Networking"
          }), " ."]
        }), "\n", _jsxs(_components.li, {
          children: ["Allowlist the ", _jsx(_components.a, {
            href: "https://hightouch.com/docs/security/networking#ip-addresses",
            children: "Hightouch IP addresses"
          }), " for your region.\n", _jsx(_components.img, {
            src: "sources/source-synapse-firewall-setup.png",
            alt: "Azure Console Dashboard with settings"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Click the ", _jsx(_components.strong, {
            children: "Save"
          }), " button in upper left corner."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "connection-configuration",
        children: "Connection configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["In Hightouch, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/sources?from=docs",
          children: [_jsx(_components.strong, {
            children: "Sources"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add source"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Azure Synapse"
        }), " and follow the steps below."]
      }), "\n", _jsx(_components.h3, {
        id: "choose-authentication-method",
        children: "Choose authentication method"
      }), "\n", _jsx(_components.p, {
        children: "When creating an Azure synapse source, you have four options to authenticate:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Password"
          }), ": Use a username and password"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Connection String"
          }), ": Provide the full URI Connection String"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Microsoft Service Principal with Client Secret"
          }), ": Use a Microsoft Service Principal created using Microsoft Entra ID with a Client Secret"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Microsoft Service Principal with Certificate"
          }), ": Use a Microsoft Service Principal created using Microsoft Entra ID with a signed Certificate"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "choose-connection-type",
        children: "Choose connection type"
      }), "\n", _jsx(ConnectionTypeSnippet, {
        source: "Azure Synapse"
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-source",
        children: "Configure your source"
      }), "\n", _jsxs(_components.p, {
        children: ["If connecting via ", _jsx(_components.strong, {
          children: "Password"
        }), " or ", _jsx(_components.strong, {
          children: "Microsoft Service Principal"
        }), ", you must first enter the following required fields into Hightouch:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Host"
          }), ": The hostname or IP address of your Azure Synapse."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Port"
          }), ": The port number of your Azure Synapse. The default is 1433, but yours may be different."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Database"
          }), ": The name of the database in your Azure Synapse."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Optionally you can enter a ", _jsx(_components.strong, {
          children: "Request timeout duration"
        }), " and whether to ", _jsx(_components.strong, {
          children: "Trust server certification"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "password",
        children: "Password"
      }), "\n", _jsx(_components.p, {
        children: "To connect using a username and password, enter the following fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Username"
          }), ": This can be your personal Azure Synapse login or a dedicated user for Hightouch."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Password"
          }), ": The password for the user specified above."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "microsoft-service-principal",
        children: "Microsoft Service Principal"
      }), "\n", _jsx(_components.p, {
        children: "To connect using a Microsoft Service Principal, use the following instructions to create a new Service Principal:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Sign in to the ", _jsx(_components.a, {
            href: "https://entra.microsoft.com/",
            children: "Microsoft Entra admin center"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["On the sidebar, select ", _jsx(_components.strong, {
            children: "Identity"
          }), " > ", _jsx(_components.strong, {
            children: "Applications"
          }), " > ", _jsx(_components.strong, {
            children: "App registrations"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "New registration"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Pick a name for your application and select ", _jsx(_components.strong, {
            children: "Accounts in this organizational directory only"
          }), " as the supported account type."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Register"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Note your newly created ", _jsx(_components.strong, {
            children: "Application (client) ID"
          }), " and ", _jsx(_components.strong, {
            children: "Directory (tenant) ID"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Add a certificate or secret"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["From here, you can opt to use either a ", _jsx(_components.strong, {
          children: "Client secret"
        }), " or a ", _jsx(_components.strong, {
          children: "Certificate"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "client-secret",
        children: "Client secret"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "Client secrets"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "New client secret"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Name your client secret and select its expiration date. To maintain access to Azure Synapse, make sure to refresh your client secret prior to its expiration."
        }), "\n", _jsxs(_components.li, {
          children: ["Note your client secret ", _jsx(_components.strong, {
            children: "Value"
          }), " now as it cannot be retrieved later. It will be used to connect Hightouch to Azure Synapse."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "certificate",
        children: "Certificate"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "Certificates"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Upload certificate"
          }), " and upload a valid signed certificate."]
        }), "\n", _jsxs(_components.li, {
          children: ["Note your certificate's ", _jsx(_components.strong, {
            children: "Thumbprint"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For more detailed instructions on service principal creation, you can check out ", _jsx(_components.a, {
          href: "https://learn.microsoft.com/en-us/entra/identity-platform/howto-create-service-principal-portal",
          children: "Microsoft's documentation"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "synapse-configuration",
        children: "Synapse configuration"
      }), "\n", _jsx(_components.p, {
        children: "Next, we need to grant the service principal access to the Azure Synapse instance:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Navigate to your instance with the ", _jsx(_components.a, {
            href: "https://portal.azure.com/#home",
            children: "Azure Portal"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["On the sidebar, select ", _jsx(_components.strong, {
            children: "Access control (IAM)"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Add"
          }), " > ", _jsx(_components.strong, {
            children: "Add role assignments"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the role you would like to assign to your service principal (Hightouch will need at least the ", _jsx(_components.strong, {
            children: "Reader"
          }), " role)"]
        }), "\n", _jsx(_components.li, {
          children: "On the next screen, select the service principal that was created above and apply the changes."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For more details, please take a look at ", _jsx(_components.a, {
          href: "https://learn.microsoft.com/en-us/azure/role-based-access-control/role-assignments-portal",
          children: "Microsoft's documentation"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "credentials",
        children: "Credentials"
      }), "\n", _jsx(_components.p, {
        children: "Finally, you can enter the following fields into Hightouch that were created during the above instructions:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Tenant ID"
          }), " (or Directory ID)"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Client ID"
          }), " (or Application ID)"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If you chose to create a ", _jsx(_components.strong, {
          children: "Client Secret"
        }), " for your service principal, you'll enter it here."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you instead chose to create a ", _jsx(_components.strong, {
          children: "Certificate"
        }), ", enter in your certificate's ", _jsx(_components.strong, {
          children: "Thumbprint"
        }), " and the associated ", _jsx(_components.strong, {
          children: "private key"
        }), ". Make sure that the private key is in PEM format and includes the header and footer."]
      }), "\n", _jsx(_components.h3, {
        id: "connection-string",
        children: "Connection String"
      }), "\n", _jsxs(_components.p, {
        children: ["If connecting via ", _jsx(_components.strong, {
          children: "Connection String"
        }), ", simply enter your connection string in this format: ", _jsx(_components.code, {
          children: "Server=server;Database=database;User Id=username;Password=password;Encrypt=boolean"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "choose-your-sync-engine",
        children: "Choose your sync engine"
      }), "\n", _jsx(SyncEngineSnippet, {
        source: "Synapse"
      }), "\n", _jsx(_components.h4, {
        id: "basic-versus-lightning-engine-comparison",
        children: "Basic versus Lightning engine comparison"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Lightning sync engine"
        }), " requires granting write access to your data warehouse, which makes its setup more involved than the Basic sync engine. However, it is more performant and reliable than the Basic engine. This makes it the ideal choice to guarantee faster syncs, especially with large data models. It also supports more features, such as ", _jsx(_components.a, {
          href: "/syncs/warehouse-sync-logs",
          children: "Warehouse Sync Logs"
        }), ", ", _jsx(_components.a, {
          href: "/match-booster/overview",
          children: "Match Booster"
        }), ", and ", _jsx(_components.a, {
          href: "https://hightouch.com/platform/identity-resolution",
          children: "Identity Resolution"
        }), "."]
      }), "\n", _jsx(EngineComparisonTable, {
        source: "Synapse"
      }), "\n", _jsx(_components.h4, {
        id: "lightning-engine-setup",
        children: "Lightning engine setup"
      }), "\n", _jsxs(_components.p, {
        children: ["Please note that Lightning engine is only supported for Azure Synapse instances that use a ", _jsx(_components.a, {
          href: "https://learn.microsoft.com/en-us/azure/synapse-analytics/sql-data-warehouse/sql-data-warehouse-overview-what-is",
          children: "dedicated SQL pool"
        }), ". Serverless Azure Synapse does not have the capabilities to support the Lightning engine."]
      }), "\n", _jsxs(_components.p, {
        children: ["To set up the Lightning engine, run the following commands. Make sure to replace ", _jsx(_components.strong, {
          children: "your_service_principal_or_user"
        }), " with the name of the service principal or user used to connect to your Synapse instance."]
      }), "\n", _jsxs(_components.p, {
        children: ["The final statement grants ", _jsx(_components.code, {
          children: "CREATE TABLE"
        }), ", which is a database-scoped permission in T-SQL. Control over a schema doesn't include it, so without this grant Hightouch can't create the tables the Lightning engine needs."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: ["IF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " (", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " sys.schemas ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WHERE"
          }), " name ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'hightouch_audit'"
          }), ")\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "BEGIN"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXEC"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "'CREATE SCHEMA [hightouch_audit]'"
          }), ");\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "END"
          }), ";\nIF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " (", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " sys.schemas ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WHERE"
          }), " name ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'hightouch_planner'"
          }), ")\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "BEGIN"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXEC"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "'CREATE SCHEMA [hightouch_planner]'"
          }), ");\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "END"
          }), ";\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " CONTROL ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " SCHEMA::hightouch_audit ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " [your_service_principal_or_user];\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " CONTROL ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " SCHEMA::hightouch_planner ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " [your_service_principal_or_user];\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TABLE"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " [your_service_principal_or_user];\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "test-your-connection",
        children: "Test your connection"
      }), "\n", _jsxs(_components.p, {
        children: ["When setting up Azure Synapse as a source for the first time, Hightouch validates your credentials and access to your database. Once the test passes, click ", _jsx(_components.strong, {
          children: "Continue"
        }), " to finish setup."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you see the following error message, you need to ", _jsx(_components.a, {
          href: "#azure-firewall-setup",
          children: "update your firewall settings"
        }), " in your Azure Console."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-vbnet",
          children: ["Cannot open server ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "'hightouch-test' request by login. Client with IP address '54.196.30.169' is not allowed to access the server. To enable access, use the Windows Azure Management Portal or run sp_set_firewall_rule on the master database to create a firewall rule for this IP address or address range. It may up take up to five minutes for this change to take effect."
          }), "\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.p, {
        children: ["Once your source configuration has passed the necessary validation, you've completed setting up Azure Synapse as a source. Next, you can set up ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "models"
        }), " to define which data you want to pull from Azure Synapse."]
      }), "\n", _jsx(_components.p, {
        children: "The Azure Synapse source supports these modeling methods:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["writing a query in ", _jsx(_components.a, {
            href: "/models/sql-editor",
            children: "the SQL editor"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["using the visual ", _jsx(_components.a, {
            href: "/models/table-selector",
            children: "table selector"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["leveraging existing ", _jsx(_components.a, {
            href: "/extensions/dbt-models",
            children: "dbt models"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(NoCommonErrors, {
        source: "true"
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
